import React, { useEffect, useMemo, useState } from "react";
import { GetServiceCaller } from "../../../services/serviceCaller";
import { API_URL } from "../../../constants";
import Select from "react-select";
import SearchBar from "../../../components/SearchBar";
import ExportToExcel from "../../../components/ExportToExcel";
import { OverlayTrigger, Table, Tooltip } from "react-bootstrap";
import { Accordion } from "react-bootstrap";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faPlus, faMinus } from "@fortawesome/free-solid-svg-icons";
import Pagination from "../../../components/Pagination";
import { useDispatch } from "react-redux";
import { updateNavbarHeading } from "../../../constants/layout/store/filterSlice";
import Dynamic_FilterForm from "../../../components/Dynamic_FilterForm";
import { FaSpinner } from "react-icons/fa";
import moment from "moment";

const RevenueByResource = () => {
  const dispatch = useDispatch();
  useEffect(() => {
    dispatch(updateNavbarHeading("Revenue By Resource (Content & Consulting)"));
  }, []);
  const [revenueData, setRevenueData] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [loading, setLoading] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(150);
  const [yearsOptions, setYearsOptions] = useState([]);
  const [apiStatus, setApiStatus] = useState(undefined);
  useEffect(() => {
    fetchYearAPI();
  }, []);

  const fetchYearAPI = async () => {
    try {
      let response;

      response = await GetServiceCaller(`${API_URL}year`);
      if (response?.status === 200) {
        const yearList = response?.data?.data?.map((ele) => ({
          value: ele?.value,
          label: ele?.text,
        }));

        const opt = yearList.reverse();
        setYearsOptions(opt);
        fetchRevenueData(opt[0]?.value);
        setFilterData([{ label: "Year", value: opt[0]?.value }]);
      }
    } catch (error) {
      console.error("Fetching Year Api ", error);
    }
  };

  const fetchRevenueData = async (id) => {
    try {
      const response = await GetServiceCaller(
        `${API_URL}rom-report/9?P_date=${id}`
      );

      if (response.status === 200) {
        setApiStatus(response?.status);
        const cleanedData = response?.data?.data?.data;
        setRevenueData(response?.data?.data?.data);

        setLoading(false);
      } else {
        console.error(
          "Failed to fetch Employee Cost. Server responded with status:",
          response.status
        );
      }
    } catch (error) {
      console.error("Error fetching Employee Cost:", error);
    }
  };

  const handlePageChange = (pageNumber) => {
    setCurrentPage(pageNumber);
  };

  // Compute the current page's data
  const startIndex = (currentPage - 1) * itemsPerPage;
  // Math.min(startIndex + itemsPerPage, filteredData.length)
  const endIndex = Math.min(startIndex + itemsPerPage, revenueData.length);
  const paginatedData = revenueData.slice(startIndex, endIndex);
  const testData = paginatedData.reduce((acc, item) => {
    // Check if the client exists, if not create an object for it
    if (!acc[item.EmpName]) {
      acc[item.EmpName] = {};
    }

    // Check if the project exists under that client, if not create an array for it
    if (!acc[item.EmpName][item.ClientName]) {
      acc[item.EmpName][item.ClientName] = [];
    }

    // Push the current item into the respective project array
    acc[item.EmpName][item.ClientName].push(item);

    return acc;
  }, {}); // Initialize accumulator as an empty object

  const [sortConfig, setSortConfig] = useState({
    key: null,
    direction: "ascending",
  });
  const sortedData = useMemo(() => {
    let sortableItems = [...paginatedData];
    if (sortConfig !== null) {
      sortableItems.sort((a, b) => {
        const valA = a[sortConfig.key] ? String(a[sortConfig.key]) : "";
        const valB = b[sortConfig.key] ? String(b[sortConfig.key]) : "";

        // Split the values into alphabetical and numeric parts
        const partsA = valA.match(/(\D+|\d+)/g) || [];
        const partsB = valB.match(/(\D+|\d+)/g) || [];

        for (let i = 0; i < Math.max(partsA.length, partsB.length); i++) {
          const partA = partsA[i] || "";
          const partB = partsB[i] || "";

          // Determine if parts are numeric or alphabetical
          const isNumericA = !isNaN(partA);
          const isNumericB = !isNaN(partB);

          if (isNumericA && isNumericB) {
            // Both parts are numeric, so compare as numbers
            const numA = parseInt(partA, 10);
            const numB = parseInt(partB, 10);
            if (numA !== numB) {
              return sortConfig.direction === "ascending"
                ? numA - numB
                : numB - numA;
            }
          } else if (isNumericA || isNumericB) {
            // One part is numeric and the other is alphabetical
            return isNumericA ? -1 : 1;
          } else {
            // Both parts are alphabetical, so compare as strings
            if (partA !== partB) {
              return sortConfig.direction === "ascending"
                ? partA.localeCompare(partB)
                : partB.localeCompare(partA);
            }
          }
        }

        return 0;
      });
    }
    return sortableItems;
  }, [testData, sortConfig]);

  useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm]);
  const filteredData = paginatedData.filter((item) =>
    Object.values(item).some((value) =>
      String(value).toLowerCase().includes(searchTerm.toLowerCase())
    )
  );

  const [activeClientKey, setActiveClientKey] = useState(null); // To track active client accordion
  const [activeProjectKey, setActiveProjectKey] = useState({}); // To track active project accordion for each client

  const handleClientToggle = (clientKey) => {
    setActiveClientKey(activeClientKey === clientKey ? null : clientKey);
  };

  const handleProjectToggle = (clientIndex, projectKey) => {
    setActiveProjectKey((prev) => ({
      ...prev,
      [clientIndex]: prev[clientIndex] === projectKey ? null : projectKey,
    }));
  };

  const [selectedYear, setSelectedYear] = useState(6736);

  const formatDataForExcel = (data) => {
    if (!data || data.length === 0) return [];

    // Group data by employee name
    const groupedByEmp = data.reduce((acc, item) => {
      const empName = item.EmpName || "";
      if (!acc[empName]) {
        acc[empName] = {
          items: [],
          totalAmount: 0,
        };
      }

      // Add invoice amount to total
      acc[empName].totalAmount += parseFloat(item.InvoiceAmount || 0);
      acc[empName].items.push(item);

      return acc;
    }, {});

    // Create formatted data
    const formattedData = [];

    // Process each employee group
    Object.entries(groupedByEmp).forEach(([empName, group]) => {
      // Skip if no items
      if (group.items.length === 0) return;

      // Create header row with employee name and total
      formattedData.push({
        EmpName: `Employee Name: ${empName}`, // Adding ** marks for Excel bold formatting
        ClientName: "",
        ProjectName: "",
        InvoiceNo: "",
        InvoiceDate: "",
        InvoiceFromDate: "",
        InvoiceToDate: "",
        InvoiceAmount: `Total Invoice Amount: ${group.totalAmount.toFixed(2)}`, // Adding ** for bold
      });

      // Add data rows with empty employee name
      group.items.forEach((item) => {
        const dataItem = {
          EmpName: "", // Empty employee name
          ClientName: item.ClientName || "",
          ProjectName: item.ProjectName || "",
          InvoiceNo: item.InvoiceNo || "",
          InvoiceDate: item.InvoiceDate || "",
          InvoiceFromDate: item.InvoiceFromDate || "",
          InvoiceToDate: item.InvoiceToDate || "",
          InvoiceAmount: item.InvoiceAmount || "",
        };

        formattedData.push(dataItem);
      });
    });

    return formattedData;
  };
  const recordsPerPageOption = [
    { value: 10, label: "10" },
    { value: 25, label: "25" },
    { value: 50, label: "50" },
    { value: 100, label: "100" },
    { value: filteredData.length, label: "All" },
  ];

  const [dropDownValue, setDropdownValue] = useState({});

  const [selectedRecordsPerPage, setSelectedRecordsPerPage] = useState(10);

  // Calculate total pages based on the current `selectedRecordsPerPage`
  const totalPages = Math.ceil(filteredData.length / selectedRecordsPerPage);

  // Data to display for the current page
  const currentPageData = filteredData.slice(
    (currentPage - 1) * selectedRecordsPerPage,
    currentPage * selectedRecordsPerPage
  );
  const handleRecordsPerPage = (e) => {
    const newRecordsPerPage = Number(e.target.value);

    // Calculate the new starting record index based on the current page
    const currentStartIndex = (currentPage - 1) * selectedRecordsPerPage;
    const newPage = Math.floor(currentStartIndex / newRecordsPerPage) + 1;

    // Update state
    setSelectedRecordsPerPage(newRecordsPerPage);
    setCurrentPage(newPage); // Adjust to the new page based on the same starting record index
    setItemsPerPage(newRecordsPerPage);
  };

  const formDatas = [
    {
      name: "Year",
      type: "Select",
      options: [...yearsOptions],
    },
  ];
  const [filterFormData, setFilterData] = useState([]);

  const handleChange = (e, name) => {
    setFilterData((prevFormData) => {
      // Check if the label already exists
      const existingLabelIndex = prevFormData.findIndex(
        (item) => item.label === name
      );

      // If the label exists, update its value
      if (existingLabelIndex !== -1) {
        const updatedFormData = prevFormData.map((item, index) =>
          index === existingLabelIndex
            ? { ...item, value: e.value !== undefined ? e.value : e }
            : item
        );
        return updatedFormData;
      } else {
        // If the label doesn't exist, add a new label-value pair
        return [
          ...prevFormData,
          {
            label: name,
            value: e.value !== undefined ? e.value : e, // Assuming e is an event object
          },
        ];
      }
    });
  };

  const handleGetData = async (client, emp, project) => {
    try {
      const response = await GetServiceCaller(
        `${API_URL}rom-report/9?P_date=${filterFormData.find((o) => o.label === "Year")
          ? filterFormData.find((o) => o.label === "Year").value
          : 0
        }`
      );

      if (response.status === 200) {
        setApiStatus(response?.status);

        const cleanedData = response?.data?.data?.data;
        setRevenueData(cleanedData);
        setLoading(false);
      } else {
        console.error(
          "Failed to fetch Employee Cost. Server responded with status:",
          response.status
        );
      }
    } catch (error) {
      console.error("Error fetching Employee Cost:", error);
    }
  };

  const handleReset = () => {
    setFilterData([]);
    fetchRevenueData();
  };
  return (
    <>
      <div className="container-fluid" id="skill_section">
        <div id="skill-table-area">
          {/* <div className="row me-3">
            <div className="col-md-3">
              <div className="row">
                <label htmlFor="refercandidatelist" className="form-label">
                  Years
                </label>
                <div className="">
                  <Select
                    name="years"
                    onChange={(e) => setSelectedYear(e.value)} // Set the selected year value
                    options={yearsOptions}
                    value={yearsOptions?.find((o) => o.value === selectedYear)}
                    placeholder="Select..."
                  />
                </div>
              </div>
            </div>

            <div className="col-md-2 mt-4">
              <div className="mb-3 mt-3">
                <button
                  className="custom-btn"
                  onClick={() => HandleFilter(selectedYear)} // Pass the selected year to the function
                >
                  Get Data
                </button>
              </div>
            </div>
          </div> */}

          <div>
            <div className="d-flex justify-content-between">
              <div className="d-flex ">
                <div className="d-flex">
                  <div className="me-1">
                    <SearchBar
                      searchTerm={searchTerm}
                      setSearchTerm={setSearchTerm}
                    />
                  </div>
                </div>
                <div className="dropdown">
                  <OverlayTrigger
                    placement="right"
                    overlay={<Tooltip id="tooltip-bottom">Filters</Tooltip>}
                  >
                    <button
                      className="dropdown-toggle"
                      type="button"
                      id="excel-btn"
                      data-bs-toggle="dropdown"
                      aria-expanded="false"
                    >
                      <i className="bi bi-funnel-fill"></i>
                    </button>
                  </OverlayTrigger>

                  <div>
                    <Dynamic_FilterForm
                      formData={formDatas || []}
                      handleChange={handleChange}
                      handleReset={handleReset}
                      handleGetData={handleGetData}
                      filterFormData={filterFormData}
                    />
                  </div>
                </div>
              </div>
              <div className="d-flex justify-content-end">
                <div className="me-1">
                  <ExportToExcel
                    data={formatDataForExcel(revenueData)}
                    filename="RevenueByResource.csv"
                  />
                </div>
              </div>
            </div>
            <Table bordered>
              <thead id="thcolor" className="text-center">
                <tr>
                  <th style={{ width: "118px" }}>Client</th>
                  <th style={{ width: "135px" }}>Project</th>
                  <th style={{ width: "118px" }}>Invoice No.</th>
                  <th style={{ width: "100px" }}>Invoice Date</th>
                  <th style={{ width: "100px" }}>From</th>
                  <th style={{ width: "100px" }}>To</th>
                  <th style={{ width: "100px" }}>Invoice Amount</th>
                </tr>
              </thead>
              {apiStatus === 200 ? (
                filteredData?.length > 0 ? (
                  <tbody>
                    {Object.entries(
                      filteredData.reduce((acc, item) => {
                        if (!acc[item.EmpName]) {
                          acc[item.EmpName] = [];
                        }
                        acc[item.EmpName].push(item);
                        return acc;
                      }, {})
                    ).map(([EmpName, items], clientIndex) => (
                      <React.Fragment key={clientIndex}>
                        <tr>
                          <td colSpan={7} className="px-0">
                            <Accordion
                              activeKey={activeClientKey}
                              onSelect={() =>
                                handleClientToggle(clientIndex.toString())
                              }
                            >
                              <Accordion.Item eventKey={clientIndex.toString()}>
                                <Accordion.Header className="AccordioninvoiceClient_header">
                                  <div className="flex-grow-1 d-flex me-2 justify-content-between text-dark">
                                    <div>{EmpName}</div>
                                    <div>
                                      {items.length > 0 && (
                                        <span>
                                          <span className="fw-bold">
                                            Total Invoice Amount:{" "}
                                          </span>
                                          {items
                                            .reduce(
                                              (sum, item) =>
                                                sum + item.TotalCost,
                                              0
                                            )
                                            .toLocaleString("en-IN")}
                                        </span>
                                      )}
                                    </div>
                                  </div>
                                  <span className="me-2 accordion_icon">
                                    <FontAwesomeIcon
                                      icon={
                                        activeClientKey ===
                                          clientIndex.toString()
                                          ? faMinus
                                          : faPlus
                                      }
                                    />
                                  </span>
                                </Accordion.Header>
                                <Accordion.Body className="no-padding">
                                  <Table bordered striped hover>
                                    <tbody>
                                      {items.map((data, subIndex) => (
                                        <tr key={subIndex} className="border">
                                          <td className="text-start" style={{ width: "100px" }}>
                                            {data.ClientName}
                                          </td>
                                          <td className="text-center" style={{ width: "100px" }}>
                                            {data.ProjectName}
                                          </td>
                                          <td className="text-center" style={{ width: "100px" }}>
                                            {data.InvoiceNo}
                                          </td>
                                          <td className="text-center" style={{ width: "80px" }}>
                                            {data.InvoiceDate ? moment(data.InvoiceDate).format("DD-MMM-YYYY") : "-"}
                                          </td>
                                          <td className="text-center" style={{ width: "80px" }}>
                                            {data.InvoiceFromDate ? moment(data.InvoiceFromDate).format("DD-MMM-YYYY") : "-"}
                                          </td>
                                          <td className="text-center" style={{ width: "80px" }}>
  {data.InvoiceToDate
  ? moment(data.InvoiceToDate, "DD-MM-YYYY").format("DD-MMM-YYYY")
  : "-"
}                                          </td>
                                          <td className="text-end" style={{ width: "80px" }}>
                                            {Number(
                                              data.InvoiceAmount
                                            ).toLocaleString("en-IN")}
                                          </td>
                                        </tr>
                                      ))}
                                    </tbody>
                                  </Table>
                                </Accordion.Body>
                              </Accordion.Item>
                            </Accordion>
                          </td>
                        </tr>
                      </React.Fragment>
                    ))}
                  </tbody>
                ) : (
                  <tbody>
                    <tr>
                      <td colSpan={7} className="text-center py-4">
                        No Data Found
                      </td>
                    </tr>
                  </tbody>
                )
              ) : (
                <tbody>
                  <tr>
                    <td colSpan={7} className="text-center py-4">
                      <div className="d-flex flex-column align-items-center">
                        <FaSpinner className="spinner mb-2" />
                        <span>Loading</span>
                      </div>
                    </td>
                  </tr>
                </tbody>
              )}
            </Table>

            <div className="d-flex justify-content-end mb-3">
              {/* <div>
                        <span className="d-flex">
                          Showing {(currentPage - 1) * selectedRecordsPerPage + 1} -{" "}
                          {Math.min(
                            currentPage * selectedRecordsPerPage,
                            groupedData.length
                          )}{" "}
                          of {groupedData.length} <span className="ms-1">results</span>
                          <select
                            value={selectedRecordsPerPage}
                            onChange={handleRecordsPerPage}
                            className="mx-2 form-select-sm"
                          >
                            {recordsPerPageOption.map((opt) => (
                              <option key={opt.value} value={opt.value}>
                                {opt.label}
                              </option>
                            ))}
                          </select>
                        </span>
                      </div> */}
              <div>
                <Pagination
                  itemsPerPage={itemsPerPage}
                  totalItems={revenueData.length}
                  currentPage={currentPage}
                  onPageChange={handlePageChange}
                />
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default RevenueByResource;
